import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {applyTableWidths, setTheadTBodyHeight} from "../Model/TableModel";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 */

const CustomExpandableTableApp = ({
                                      tableId,
                                      checkbox,
                                      thead,
                                      tbody,
                                      inModal,
                                      onRowExpand,
                                      withoutHeight
                                  }) => {
    const [openRowId, setOpenRowId] = useState(null);
    const elementRef = useRef(null);
    const tbodyRef = useRef(null);
    const rafRef = useRef(null);
    const { t } = useTranslation();
    const rows = Array.isArray(tbody) ? tbody : (tbody?.body || []);

    /**
     * Executes the column width calculation logic for the table.
     *
     * Delegates the width computation and distribution to the `applyTableWidths`
     * utility function, ensuring widths are applied based on current data and layout.
     *
     * @returns {void}
     */
    const applyColumnWidths = useCallback(() => {
        applyTableWidths({ tableId, rows, checkbox, thead });
    }, [tableId, thead, rows, checkbox]);

    /**
     * Initializes layout-related CSS variables and performs a one-time
     * column width calculation after the first render cycle.
     *
     * - Applies dynamic positioning using `setTheadTBodyHeight`
     * - Ensures column widths are calculated only once using a ref flag
     * - Uses requestAnimationFrame to defer execution until layout is stable
     *
     * Dependencies:
     * - applyColumnWidths: recalculated if table structure changes
     * - inModal: affects layout positioning
     * - withoutHeight: controls height behavior
     */
    useEffect(() => {
        setTheadTBodyHeight({withoutHeight, elementRef, tbodyRef, inModal});

        if (rafRef.current) cancelAnimationFrame(rafRef.current);

        rafRef.current = requestAnimationFrame(() => {
            applyColumnWidths();
        });

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [applyColumnWidths, inModal, withoutHeight]);

    /**
     * Toggles the expansion state of a table row.
     *
     * If the selected row is not currently expanded, it triggers the optional
     * `onRowExpand` callback before updating the state.
     *
     * @param {string|number} rowId - Unique identifier of the row to toggle.
     * @param {Object} row - Full row data object associated with the rowId.
     * @returns {void}
     */
    const toggleRow = (rowId, row) => {
        const isExpanding = openRowId !== rowId;

        if (isExpanding && onRowExpand) {
            onRowExpand(row);
        }
        setOpenRowId(isExpanding ? rowId : null);
    };

    return {
        t,
        elementRef,
        tbodyRef,
        openRowId,
        toggleRow
    };
};

export default CustomExpandableTableApp;
